
import React, { useState } from 'react';
import { Instagram, Youtube, Mail, BookOpen, CheckCircle, Loader2, Bell } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { subscribeToWebPush } from '../services/webPushService';
import LegalModal from './LegalModal';

const Footer: React.FC = () => {
  const { sections } = useSite();
  const { t } = useLanguage();
  const { subscribeAndSignInWithEmail, currentUser, isMember } = useAuth();

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);

  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalTab, setLegalTab] = useState<'privacy' | 'terms'>('privacy');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;

    setSubscribing(true);
    setSubscribeError(null);

    try {
      const res = await subscribeAndSignInWithEmail(newsletterEmail);
      // Garante replicação imediata via backend
      await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail.trim(), source: 'footer' })
      }).catch(() => {});

      // Ativa Web Push atrelado ao e-mail
      subscribeToWebPush(['articles', 'chapters', 'all'], newsletterEmail.trim()).catch(() => {});

      if (res.success) {
        setSubscribeSuccess(true);
        setNewsletterEmail('');
        setTimeout(() => setSubscribeSuccess(false), 8000);
      } else {
        setSubscribeError(res.error || 'Erro ao realizar inscrição.');
      }
    } catch (err) {
      // Fallback via API
      try {
        const apiRes = await fetch('/api/newsletter/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: newsletterEmail.trim(), source: 'footer_fallback' })
        });
        if (apiRes.ok) {
          setSubscribeSuccess(true);
          setNewsletterEmail('');
          setTimeout(() => setSubscribeSuccess(false), 8000);
          return;
        }
      } catch {}
      setSubscribeError('Erro ao se inscrever. Tente novamente.');
    } finally {
      setSubscribing(false);
    }
  };

  const openLegal = (tab: 'privacy' | 'terms') => {
    setLegalTab(tab);
    setLegalModalOpen(true);
  };

  return (
    <footer className="pt-16 pb-28 md:pb-8 relative z-10">
      {/* Dynamic Footer Sections */}
      {sections.filter(s => s.location === 'footer').map(section => (
        <div key={section.id} className="container mx-auto px-6 mb-12">
          <div dangerouslySetInnerHTML={{ __html: section.contentHtml }} />
        </div>
      ))}
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div className="col-span-1 md:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <div className="relative flex items-center justify-center w-8 h-8 rounded-full bg-black/40 backdrop-blur-md border border-white/10 group-hover:border-primary/50 transition-colors duration-500 overflow-hidden">
                <img 
                  src="/images/nova-logo.webp" 
                  alt="Literafan Logo" 
                  width="20"
                  height="20"
                  loading="lazy"
                  decoding="async"
                  className="w-5 h-5 object-contain relative z-10 group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <span className="font-serif text-2xl font-bold text-stone-100">
                Litera<span className="text-primary italic">fan</span>
              </span>
            </Link>
            <p className="text-stone-400 text-sm leading-relaxed mb-6">
              {t('footer.tagline', 'Uma comunidade dedicada aos amantes da literatura. Conectando leitores, explorando mundos e compartilhando aventuras.')}
            </p>
            <div className="flex space-x-3 mb-6">
              <a href="https://discord.gg/dQrr92QpM" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center text-stone-400 hover:bg-[#5865F2] hover:text-white transition-all duration-300" title="Discord" aria-label="Comunidade Literafan no Discord">
                <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994.021-.041.001-.09-.041-.106a13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.929 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.894.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.028zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
              </a>
              <a href="https://www.instagram.com/literafan?igsh=eDhxejBkbDZlb2ty" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center text-stone-400 hover:bg-pink-600 hover:text-white transition-all duration-300" title="Instagram" aria-label="Perfil do Literafan no Instagram">
                <Instagram size={18} />
              </a>
              <a href="https://www.youtube.com/@literafan" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center text-stone-400 hover:bg-primary hover:text-white transition-all duration-300" title="YouTube" aria-label="Canal do Literafan no YouTube">
                <Youtube size={18} />
              </a>
              <a href="mailto:contato@literafan.com" className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center text-stone-400 hover:bg-stone-700 hover:text-white transition-all duration-300" title="E-mail" aria-label="Enviar e-mail para contato@literafan.com">
                <Mail size={18} />
              </a>
            </div>
          </div>

          <div className="col-span-1">
            <h3 className="font-serif font-bold text-lg mb-6 text-stone-100">{t('footer.explore', 'Explorar')}</h3>
            <ul className="space-y-3">
              <li><Link to="/" className="text-stone-400 hover:text-primary transition-colors text-sm">{t('nav.home', 'Início')}</Link></li>
              <li><Link to="/artigos" className="text-stone-400 hover:text-primary transition-colors text-sm">{t('footer.articles_reviews', 'Artigos & Resenhas')}</Link></li>
              <li><Link to="/livraria" className="text-stone-400 hover:text-primary transition-colors text-sm">{t('footer.curated_bookstore', 'Livraria Literafan')}</Link></li>
              <li><Link to="/comunidade" className="text-stone-400 hover:text-primary transition-colors text-sm">{t('footer.community_forum', 'Fórum da Comunidade')}</Link></li>
              <li><Link to="/sobre" className="text-stone-400 hover:text-primary transition-colors text-sm">Sobre Nós</Link></li>
              <li><Link to="/contato" className="text-stone-400 hover:text-primary transition-colors text-sm">Fale Conosco</Link></li>
            </ul>
          </div>

          <div className="col-span-1">
            <h3 className="font-serif font-bold text-lg mb-6 text-stone-100">{t('footer.categories', 'Categorias')}</h3>
            <ul className="space-y-3">
              <li><span className="text-stone-400 hover:text-primary transition-colors text-sm cursor-pointer">Ficção Literária</span></li>
              <li><span className="text-stone-400 hover:text-primary transition-colors text-sm cursor-pointer">Clássicos</span></li>
              <li><span className="text-stone-400 hover:text-primary transition-colors text-sm cursor-pointer">Fantasia & Aventura</span></li>
              <li><span className="text-stone-400 hover:text-primary transition-colors text-sm cursor-pointer">Não-Ficção</span></li>
            </ul>
          </div>

          <div className="col-span-1">
            <h3 className="font-serif font-bold text-lg mb-6 text-stone-100">{t('footer.newsletter', 'Newsletter')}</h3>
            <p className="text-stone-400 text-sm mb-4">
              {t('footer.newsletter_desc', 'Inscreva-se na nossa newsletter para receber notificações de novas publicações em primeira mão, além de curtir e comentar em artigos e fanarts.')}
            </p>

            {subscribeSuccess ? (
              <div className="p-4 bg-emerald-950/50 border border-emerald-800/80 rounded-2xl text-emerald-300 text-xs flex items-center gap-3">
                <CheckCircle size={20} className="text-emerald-400 flex-shrink-0" />
                <div>
                  <p className="font-bold text-white">Inscrição Confirmada!</p>
                  <p className="text-[11px] text-stone-300 mt-0.5">
                    Notificações ativadas! Você receberá avisos a cada nova publicação e já pode interagir com os artigos e a comunidade.
                  </p>
                </div>
              </div>
            ) : isMember ? (
              <div className="p-4 bg-stone-900/80 border border-stone-800 rounded-2xl text-xs text-stone-300 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-950/80 border border-emerald-600/40 text-emerald-400 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={16} />
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold block flex items-center gap-1.5">
                    <span>Membro Ativo</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  </span>
                  <span className="text-stone-200 font-medium block truncate max-w-[200px]">
                    {currentUser?.displayName || currentUser?.email}
                  </span>
                  <span className="text-[10px] text-stone-400 block mt-0.5 flex items-center gap-1">
                    <Bell size={10} className="text-primary" />
                    Notificações de novas publicações ativas
                  </span>
                </div>
              </div>
            ) : (
              <form className="flex flex-col space-y-3" onSubmit={handleSubscribe}>
                <input 
                  type="email" 
                  required
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder={t('footer.your_email', 'Seu melhor e-mail')} 
                  className="w-full px-4 py-3 bg-stone-800 border border-stone-700 rounded-full focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-sm transition-all text-stone-200 placeholder-stone-500"
                />
                {subscribeError && (
                  <p className="text-xs text-red-400 px-2">{subscribeError}</p>
                )}
                <button 
                  type="submit"
                  disabled={subscribing}
                  className="w-full px-4 py-3 bg-stone-200 text-stone-900 text-xs font-bold rounded-full hover:bg-primary hover:text-white transition-colors uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {subscribing ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Processando...</span>
                    </>
                  ) : (
                    <>
                      <Bell size={13} className="text-primary" />
                      <span>{t('footer.subscribe', 'Inscrever-se & Ativar Notificações')}</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-stone-500 border-t border-stone-900">
          <p>&copy; {new Date().getFullYear()} Literafan. {t('footer.rights', 'Todos os direitos reservados.')}</p>
          <div className="flex flex-wrap gap-4 mt-4 md:mt-0 items-center justify-center text-xs">
            <Link 
              to="/privacidade" 
              className="hover:text-primary transition-colors text-stone-400 hover:underline"
            >
              {t('footer.privacy', 'Privacidade & Cookies (LGPD)')}
            </Link>
            <span className="text-stone-700">&bull;</span>
            <Link 
              to="/termos" 
              className="hover:text-primary transition-colors text-stone-400 hover:underline"
            >
              {t('footer.terms', 'Termos de Uso & Comunidade')}
            </Link>
            <span className="text-stone-700">&bull;</span>
            <Link 
              to="/sobre" 
              className="hover:text-primary transition-colors text-stone-400 hover:underline"
            >
              Sobre Nós
            </Link>
            <span className="text-stone-700">&bull;</span>
            <Link 
              to="/contato" 
              className="hover:text-primary transition-colors text-stone-400 hover:underline"
            >
              Contato
            </Link>
          </div>
        </div>
      </div>

      {/* Legal Modal */}
      <LegalModal 
        isOpen={legalModalOpen} 
        onClose={() => setLegalModalOpen(false)} 
        initialTab={legalTab} 
      />
    </footer>
  );
};

export default Footer;
